import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AppBar,
  Body,
  Button,
  Card,
  ErrorBox,
  Label,
  ListCard,
  Screen,
  Spinner,
  cx,
} from '../components/ui';
import { useGroup, useGroupToken } from '../hooks/useGroup';
import { api, ApiError } from '../lib/api';
import { removeStoredGroup } from '../lib/storage';

/** グループ設定（§4.1 G-04）。名前の変更と削除、各設定への入口 */
export function SettingsPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const token = useGroupToken(groupId);
  const group = useGroup(groupId, token);
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const loaded = group.data;
  useEffect(() => {
    if (loaded && !name) setName(loaded.group.name);
  }, [loaded, name]);

  const rename = useMutation({
    mutationFn: () => api.updateGroup(groupId!, token!, { name: name.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['group', groupId] });
    },
  });

  const destroy = useMutation({
    mutationFn: () => api.deleteGroup(groupId!, token!),
    onSuccess: () => {
      // 端末側の参加記録も消す。残しておくと開けないカードが並ぶ
      removeStoredGroup(groupId!);
      queryClient.clear();
      navigate('/', { replace: true });
    },
  });

  if (!token) {
    return (
      <Screen>
        <AppBar title="グループ設定" backTo="/" />
        <Body>
          <ErrorBox message="この端末に参加情報がありません。共有URLから開いてください。" />
        </Body>
      </Screen>
    );
  }

  if (group.isLoading || !loaded) {
    return (
      <Screen>
        <AppBar title="グループ設定" backTo={`/g/${groupId}`} />
        <Body>
          <Spinner />
        </Body>
      </Screen>
    );
  }

  const nameChanged = name.trim() !== loaded.group.name && name.trim().length > 0;

  return (
    <Screen>
      <AppBar title="グループ設定" backTo={`/g/${groupId}`} />

      <Body className="gap-2.5">
        {/* --- 名前 --- */}
        <div>
          <Label>グループ名</Label>
          <div className="mt-1 flex items-stretch gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              className="min-w-0 flex-1 rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] outline-none focus:border-felt"
            />
            <button
              type="button"
              disabled={!nameChanged || rename.isPending}
              onClick={() => rename.mutate()}
              className="shrink-0 rounded-lg bg-felt px-5 text-[14px] font-bold text-bone-hi transition active:scale-[0.99] disabled:bg-off disabled:text-off-ink"
            >
              {rename.isPending ? '保存中' : '保存'}
            </button>
          </div>
          {rename.isSuccess && !nameChanged && (
            <div className="mt-1.5 text-xs text-ok">変更しました</div>
          )}
          {rename.isError && (
            <div className="mt-1.5">
              <ErrorBox
                message={rename.error instanceof ApiError ? rename.error.message : '保存できませんでした'}
              />
            </div>
          )}
        </div>

        {/* --- 各設定への入口 --- */}
        <Label className="mt-1">設定</Label>
        <ListCard className="py-1">
          {[
            { to: `/g/${groupId}/members`, label: 'メンバー', sub: `${loaded.members.length}人` },
            {
              to: `/g/${groupId}/rules`,
              label: 'ルール',
              sub: loaded.rules.find((r) => r.isDefault)?.name ?? loaded.rules[0]?.name ?? '',
            },
          ].map((row, i) => (
            <Link
              key={row.to}
              to={row.to}
              className={cx('flex items-center gap-3 py-3.5', i > 0 && 'border-t border-line')}
            >
              <span className="text-[14.5px] font-bold">{row.label}</span>
              <span className="ml-auto text-[12px] text-ink-faint">{row.sub}</span>
              <span className="text-base text-ink-faint">›</span>
            </Link>
          ))}
        </ListCard>

        {/* --- グループ情報 --- */}
        <Card className="mt-1">
          <Label>このグループ</Label>
          <div className="mt-2 space-y-1 text-[12.5px] text-ink-soft">
            <div>対局数 {loaded.group.matchCount}件</div>
            <div>作成日 {new Date(loaded.group.createdAt).toLocaleDateString('ja-JP')}</div>
          </div>
          <div className="mt-3 text-[11px] leading-relaxed text-ink-faint">
            12か月アクセスがないグループは自動的に削除されます。開くたびに期限は延長されます。
          </div>
        </Card>

        {/* --- 削除（G-04 確認ダイアログ必須） --- */}
        <Label className="mt-3">グループの削除</Label>
        {!deleting ? (
          <Button variant="ghost" onClick={() => setDeleting(true)} className="border-red text-red">
            このグループを削除する
          </Button>
        ) : (
          <Card className="border-red">
            <div className="text-[13.5px] font-bold text-red">本当に削除しますか</div>
            <div className="mt-1.5 text-[12.5px] leading-relaxed text-ink-soft">
              {loaded.group.matchCount}件の対局記録とメンバー、ルールがすべて消えます。
              共有URLを持っている全員が開けなくなります。<b>元に戻せません。</b>
            </div>
            <div className="mt-3">
              <Label>確認のため、グループ名を入力してください</Label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={loaded.group.name}
                className="mt-1 w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[15px] outline-none focus:border-red"
              />
            </div>
            {destroy.isError && (
              <div className="mt-3">
                <ErrorBox
                  message={
                    destroy.error instanceof ApiError ? destroy.error.message : '削除できませんでした'
                  }
                />
              </div>
            )}
            <div className="mt-3 flex flex-col gap-2">
              <Button
                onClick={() => destroy.mutate()}
                disabled={confirmText.trim() !== loaded.group.name || destroy.isPending}
                className="bg-red text-white disabled:bg-off disabled:text-off-ink"
              >
                {destroy.isPending ? '削除しています…' : '完全に削除する'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleting(false);
                  setConfirmText('');
                }}
              >
                やめる
              </Button>
            </div>
          </Card>
        )}
      </Body>
    </Screen>
  );
}
